import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type AccountingSummary = {
  organization_id: string;
  draft_documents: number | string;
  issued_documents: number | string;
  cancelled_documents: number | string;
  issued_total: number | string;
  posted_entries: number | string;
  open_periods: number | string;
  sunat_transmission_enabled: boolean;
};

type AccountingPeriod = {
  id: string;
  period_code: string;
  period_name: string;
  date_from: string;
  date_to: string;
  status: string;
  opened_at: string;
  closed_at: string | null;
  document_count: number | string;
  entry_count: number | string;
};

type AccountingDocument = {
  id: string;
  full_number: string | null;
  document_type: "invoice" | "receipt" | "credit_note" | "debit_note";
  status: "draft" | "issued" | "cancelled";
  sunat_status: string;
  issue_date: string;
  due_date: string | null;
  customer_name: string | null;
  customer_document_number: string | null;
  currency_code: string;
  taxable_amount: number | string;
  tax_total: number | string;
  total: number | string;
  quotation_id: string | null;
  service_order_id: string | null;
  sale_id: string | null;
  related_document_id: string | null;
  updated_at: string;
};

const DOCUMENT_LABELS: Record<AccountingDocument["document_type"], string> = {
  invoice: "Factura",
  receipt: "Boleta",
  credit_note: "Nota de crédito",
  debit_note: "Nota de débito",
};

const SUNAT_STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  not_applicable: "No aplica",
  accepted: "Aceptado",
  rejected: "Rechazado",
  processing: "Procesando",
  error: "Error",
  cancelled: "Anulado",
};

const STATUS_LABELS: Record<AccountingDocument["status"], string> = {
  draft: "Borrador",
  issued: "Emitido",
  cancelled: "Anulado",
};

function number(value: number | string | undefined) {
  return Number(value || 0);
}

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(number(value));
}

export function AccountingPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [summary, setSummary] = useState<AccountingSummary | null>(null);
  const [periods, setPeriods] = useState<AccountingPeriod[]>([]);
  const [documents, setDocuments] = useState<AccountingDocument[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const loadAccounting = useCallback(
    async (query: string) => {
      if (!organizationId) {
        setSummary(null);
        setPeriods([]);
        setDocuments([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      const [summaryResult, periodsResult, documentsResult] = await Promise.all([
        supabase.rpc("get_accounting_ui_summary", {
          p_organization_id: organizationId,
        }),
        supabase.rpc("list_accounting_periods_ui", {
          p_organization_id: organizationId,
        }),
        supabase.rpc("list_accounting_documents_ui", {
          p_organization_id: organizationId,
          p_search: query.trim() || null,
        }),
      ]);

      const firstError =
        summaryResult.error ?? periodsResult.error ?? documentsResult.error;

      if (firstError) {
        setLoadError(firstError.message);
      }

      setSummary(
        summaryResult.error
          ? null
          : (((summaryResult.data ?? [])[0] as AccountingSummary | undefined) ??
              null),
      );
      setPeriods(
        periodsResult.error
          ? []
          : ((periodsResult.data ?? []) as AccountingPeriod[]),
      );
      setDocuments(
        documentsResult.error
          ? []
          : ((documentsResult.data ?? []) as AccountingDocument[]),
      );

      setLoading(false);
    },
    [organizationId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadAccounting(search);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadAccounting, search]);

  useEffect(() => {
    setSearch("");
    setLoadError(null);
  }, [organizationId]);

  const openPeriod = useMemo(
    () => periods.find((period) => period.status === "open") ?? null,
    [periods],
  );

  async function issueDocument(
    document: AccountingDocument,
    onCredit: boolean,
  ) {
    setActionBusyId(document.id);
    setActionError(null);
    setActionSuccess(null);

    const result = onCredit
      ? await supabase.rpc("issue_accounting_document_on_credit", {
          p_document_id: document.id,
        })
      : await supabase.rpc("issue_accounting_document", {
          p_document_id: document.id,
        });

    setActionBusyId(null);

    if (result.error) {
      setActionError(result.error.message);
      return;
    }

    setActionSuccess(
      onCredit
        ? "Comprobante emitido y cuenta por cobrar creada."
        : "Comprobante emitido.",
    );
    await loadAccounting(search);
  }

  async function ensureReceivable(document: AccountingDocument) {
    setActionBusyId(document.id);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc(
      "ensure_ar_receivable_for_document",
      {
        p_document_id: document.id,
      },
    );

    setActionBusyId(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setActionSuccess("Cuenta por cobrar sincronizada.");
    await loadAccounting(search);
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para revisar Contabilidad.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Contabilidad</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Comprobantes, periodos y asientos del núcleo contable.</p>
        </div>
        <div className="quotation-actions">
          <Link className="ghost-button" to="/contabilidad/asientos">
            Contabilidad General
          </Link>
          <Link className="ghost-button" to="/contabilidad/compras-recibidas">
            Compras recibidas
          </Link>
          <Link className="ghost-button" to="/reportes">
            EEFF y reportes
          </Link>
        </div>
      </header>

      {summary && !summary.sunat_transmission_enabled ? (
        <section className="sunat-paused-banner">
          <div>
            <strong>Transmisión SUNAT en pausa</strong>
            <span>
              Los datos contables permanecen operativos, pero este panel no
              transmite comprobantes a SUNAT.
            </span>
          </div>
          <span className="status-badge status-badge-expired">Deshabilitada</span>
        </section>
      ) : null}

      <section className="accounting-summary-grid">
        <div className="metric-card">
          <span>Comprobantes emitidos</span>
          <strong>{loading ? "…" : number(summary?.issued_documents)}</strong>
        </div>
        <div className="metric-card">
          <span>Borradores</span>
          <strong>{loading ? "…" : number(summary?.draft_documents)}</strong>
        </div>
        <div className="metric-card">
          <span>Total emitido</span>
          <strong>{loading ? "…" : money(summary?.issued_total ?? 0)}</strong>
        </div>
        <div className="metric-card">
          <span>Asientos posteados</span>
          <strong>{loading ? "…" : number(summary?.posted_entries)}</strong>
        </div>
        <div className="metric-card">
          <span>Periodos abiertos</span>
          <strong>{loading ? "…" : number(summary?.open_periods)}</strong>
        </div>
      </section>

      {loadError ? (
        <div className="inline-alert" role="alert">
          {loadError}
        </div>
      ) : null}

      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="form-success">{actionSuccess}</div>
      ) : null}

      <section className="panel-card accounting-period-panel">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Periodos</span>
            <h2>{openPeriod ? openPeriod.period_name : "Sin periodo abierto"}</h2>
          </div>
        </div>

        <div className="accounting-period-grid">
          {periods.map((period) => (
            <article className="accounting-period-card" key={period.id}>
              <div>
                <span
                  className={
                    period.status === "open"
                      ? "status-badge status-badge-approved"
                      : "status-badge"
                  }
                >
                  {period.status === "open" ? "Abierto" : period.status}
                </span>
                <h3>{period.period_name}</h3>
                <p>
                  {period.date_from} → {period.date_to}
                </p>
              </div>

              <dl className="accounting-period-meta">
                <div>
                  <dt>Comprobantes</dt>
                  <dd>{number(period.document_count)}</dd>
                </div>
                <div>
                  <dt>Asientos</dt>
                  <dd>{number(period.entry_count)}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : documents.length}</strong>
          <span> comprobantes</span>
        </div>
        <input
          type="search"
          placeholder="Buscar por número, cliente o documento"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {!loading && documents.length === 0 && !loadError ? (
        <section className="empty-state">
          <strong>No hay comprobantes para mostrar.</strong>
          <p>Los documentos contables aparecerán aquí cuando sean creados.</p>
        </section>
      ) : null}

      <section className="accounting-document-grid" aria-label="Comprobantes">
        {documents.map((document) => (
          <article className="accounting-document-card" key={document.id}>
            <div className="accounting-document-heading">
              <div>
                <span
                  className={
                    document.status === "issued"
                      ? "status-badge status-badge-approved"
                      : document.status === "cancelled"
                        ? "status-badge status-badge-cancelled"
                        : "status-badge"
                  }
                >
                  {STATUS_LABELS[document.status]}
                </span>
                <h2>{document.full_number ?? "Sin número"}</h2>
                <p>{DOCUMENT_LABELS[document.document_type]}</p>
              </div>

              <strong>{money(document.total, document.currency_code)}</strong>
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Cliente</dt>
                <dd>{document.customer_name ?? "—"}</dd>
              </div>
              <div>
                <dt>Emisión</dt>
                <dd>{document.issue_date}</dd>
              </div>
              <div>
                <dt>Base</dt>
                <dd>
                  {money(document.taxable_amount, document.currency_code)}
                </dd>
              </div>
              <div>
                <dt>IGV</dt>
                <dd>{money(document.tax_total, document.currency_code)}</dd>
              </div>
            </dl>

            <div className="accounting-sunat-state">
              <span>Estado fiscal</span>
              <strong>{SUNAT_STATUS_LABELS[document.sunat_status?.toLowerCase()] ?? document.sunat_status}</strong>
            </div>

            {document.quotation_id || document.service_order_id || document.sale_id ? (
              <div className="quotation-actions">
                {document.sale_id ? (
                  <Link
                    className="compact-button"
                    to={`/ventas/${document.sale_id}`}
                  >
                    Ver venta
                  </Link>
                ) : null}
                {document.quotation_id ? (
                  <Link
                    className="compact-button"
                    to={`/cotizaciones/${document.quotation_id}`}
                  >
                    Ver cotización
                  </Link>
                ) : null}
                {document.service_order_id ? (
                  <Link
                    className="compact-button"
                    to={`/taller/${document.service_order_id}`}
                  >
                    Ver orden de servicio
                  </Link>
                ) : null}
              </div>
            ) : null}

            {document.status === "draft" ? (
              <div className="quotation-actions">
                <button
                  className="compact-button"
                  type="button"
                  disabled={actionBusyId === document.id}
                  onClick={() => void issueDocument(document, false)}
                >
                  Emitir
                </button>

                {document.due_date &&
                ["invoice", "receipt", "debit_note"].includes(
                  document.document_type,
                ) ? (
                  <button
                    className="compact-button"
                    type="button"
                    disabled={actionBusyId === document.id}
                    onClick={() => void issueDocument(document, true)}
                  >
                    Emitir a crédito
                  </button>
                ) : null}
              </div>
            ) : null}

            {document.status === "issued" &&
            document.due_date &&
            ["invoice", "receipt", "debit_note"].includes(
              document.document_type,
            ) ? (
              <div className="quotation-actions">
                <button
                  className="compact-button"
                  type="button"
                  disabled={actionBusyId === document.id}
                  onClick={() => void ensureReceivable(document)}
                >
                  Sincronizar CxC
                </button>
              </div>
            ) : null}
          </article>
        ))}
      </section>
    </div>
  );
}
